import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { CITY_DISTRICTS } from '@/data/cityDistricts';
import { WARSAW_MSI_REJONY } from '@/data/warsawMsiRejony';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import { areaKey, type SearchArea } from '@/lib/areaSearch';
import { MAX_OBSZAROW, dodajObszar, dzielnicaObszaru, usunObszar } from '@/lib/areaSelection';
import { useDotyk } from '@/hooks/use-mobile';
import { klasyZastapienia, useBlyskZastapienia } from '@/hooks/useBlyskZastapienia';

/**
 * Wybór dzielnic i osiedli — JEDEN komponent, dwa wejścia (plan §1.17, E2.2; makieta
 * `docs/wireframes/serp-filter-panel.html`, wiersz Lokalizacja): przycisk „Dzielnice” w polu
 * wyszukiwania (pulpit: dymek; telefon: sekcja nakładki obszaru) i wiersz Lokalizacja w panelu
 * filtrów. Oba edytują ten sam stan (`SourcePostFilters.areas`), więc wybór w jednym miejscu
 * jest od razu widoczny w drugim.
 *
 * Kaskada: „Dowolna dzielnica (cała Warszawa)” na początku, podświetlona, gdy nic nie wybrano;
 * 18 dzielnic w dwóch kolumnach; dotknięcie dzielnicy wybiera ją I rozwija pod nią jej osiedla;
 * szewron rozwija bez zmiany wyboru (żeby zajrzeć do osiedli JUŻ wybranej dzielnicy, nie
 * zdejmując jej).
 *
 * DZIELNICA WYBRANA CZĘŚCIOWO (plan §1.91, propozycja A, Maciek 20.10): kreska w kratce (stan
 * „nieokreślony”, `aria-pressed="mixed"`) i pod nazwą „2 z 3 osiedli” w kolorze głównym. Szare
 * tło wiersza znaczy tylko „ten wiersz jest rozwinięty”. Cała dzielnica nie podświetla swoich
 * osiedli (obejmuje je), a jej rozwinięte pudełko mówi, że osiedle zawęzi wybór.
 *
 * PODGLĄD I BŁYSK jak w polu wyszukiwania (prototyp 19.10): najechanie myszą albo fokus z
 * klawiatury na dzielnicę lub osiedle, które coś ZASTĄPI, przygasza to, co zniknie — tu (kratka
 * dzielnicy, pastylki osiedli) i u rodzica przez `onPodglad` (chipy nad listą). Na dotyku podglądu
 * nie ma: palec nie „najeżdża”, a emulowany hover zostawałby po dotknięciu. Po zastąpieniu
 * wynik krótko świeci (`useBlyskZastapienia`, ten sam zegar co w polu).
 *
 * Reguły zbiorów NIE siedzą tutaj: `dodajObszar` / `usunObszar` (src/lib/areaSelection.ts,
 * Piotr 26.09, potwierdzone przez Maćka 13.10) — osiedle zastępuje swoją dzielnicę, dzielnica
 * pochłania swoje osiedla, osiedla z różnych dzielnic się sumują, najwyżej 8 obszarów. Ten
 * komponent tylko je woła; testy reguł przez komponent są w `WyborDzielnic.test.tsx`.
 */

interface Props {
  areas: SearchArea[];
  onChange: (next: SearchArea[]) => void;
  /**
   * Dodatkowa treść pod osiedlami rozwiniętej dzielnicy — nakładka na telefonie dokłada tu
   * stacje metra tej dzielnicy (decyzja Piotra 26.09: stacje w kontekście dzielnicy, nie chmurą).
   */
  dodatkiDzielnicy?: (dzielnica: string) => React.ReactNode;
  /**
   * Podgląd zastąpienia dla chipów rodzica: klucze wybranych obszarów, które zniknęłyby po
   * kliknięciu dzielnicy albo osiedla pod kursorem; `[]`, gdy nic. Woła tylko przy zmianie.
   */
  onPodglad?: (zastapione: string[]) => void;
  /**
   * Same picker with the estates level hidden: no chevrons, no expansion, a tap only toggles the
   * district. The homepage search uses it (Maciek 21.10: main districts are enough there).
   */
  bezOsiedli?: boolean;
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;
type StanKratki = 'pelna' | 'czesciowa' | 'pusta';

/** Dzielnice w kolejności alfabetycznej po polsku — jak w makiecie (Śródmieście przed Targówkiem).
 *  Warszawa wprost, jak w areaSearch.ts: osiedla to rejony MSI, których inne miasta nie mają. */
const DZIELNICE = [...CITY_DISTRICTS['Warszawa'].districts.map((d) => d.name)].sort((a, b) =>
  a.localeCompare(b, 'pl'),
);

const OSIEDLA: ReadonlyMap<string, string[]> = WARSAW_MSI_REJONY.reduce((m, r) => {
  m.set(r.district, [...(m.get(r.district) ?? []), r.name]);
  return m;
}, new Map<string, string[]>());

/** Kratka wyboru: pełna (✓), częściowa (kreska, stan nieokreślony) albo pusta. Rysowana, nie
 *  `<input type=checkbox>` — ta sama rola w obu wejściach. `przygaszona`: podgląd, że kliknięcie
 *  osiedla zdejmie tę dzielnicę. */
function Kratka({ stan, przygaszona }: { stan: StanKratki; przygaszona?: boolean }) {
  return (
    <span
      aria-hidden
      data-stan={stan}
      data-przygaszona={przygaszona || undefined}
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-opacity duration-200 ${
        stan === 'pelna'
          ? 'border-primary bg-primary text-primary-foreground'
          : stan === 'czesciowa'
            ? 'border-primary bg-background'
            : 'border-border'
      } ${przygaszona ? 'opacity-40' : ''}`}
    >
      {stan === 'pelna' && <Check className="h-3 w-3" strokeWidth={3} />}
      {stan === 'czesciowa' && <span data-testid="kreska-czesciowa" className="h-0.5 w-2 rounded-full bg-primary" />}
    </span>
  );
}

/** Zdarzenia podglądu: mysz i klawiatura. Ten sam cel dla obu, zdejmowany tylko przez siebie. */
const zdarzeniaPodgladu = (cel: SearchArea, ustaw: React.Dispatch<React.SetStateAction<SearchArea | null>>) => {
  const zdejmij = () => ustaw((c) => (c && areaKey(c) === areaKey(cel) ? null : c));
  return { onMouseEnter: () => ustaw(cel), onMouseLeave: zdejmij, onFocus: () => ustaw(cel), onBlur: zdejmij };
};

/** Chip osiedla — ten sam język co pastylki panelu (FilterChip): 40 px na telefonie, 36 na pulpicie. */
function ChipOsiedla({
  nazwa,
  aktywny,
  przygaszony,
  blysk,
  onClick,
  podglad,
}: {
  nazwa: string;
  aktywny: boolean;
  przygaszony: boolean;
  blysk: boolean;
  onClick: () => void;
  podglad: ReturnType<typeof zdarzeniaPodgladu>;
}) {
  return (
    <button
      type="button"
      aria-pressed={aktywny}
      onClick={onClick}
      {...podglad}
      data-przygaszony={przygaszony || undefined}
      data-blysk={blysk || undefined}
      className={`inline-flex h-10 items-center rounded-full border px-3 text-base font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring md:h-9 md:text-sm ${
        aktywny ? 'border-primary' : 'border-border hover:bg-muted'
      } ${klasyZastapienia({ przygaszony, blysk, tlo: aktywny ? 'bg-primary/5' : '' })}`}
    >
      {nazwa}
    </button>
  );
}

export function WyborDzielnic({ areas, onChange, dodatkiDzielnicy, onPodglad, bezOsiedli = false }: Props) {
  const { t } = useTranslation() as { t: Translate };
  const [rozwinieta, setRozwinieta] = useState<string | null>(null);
  const [limit, setLimit] = useState(false);
  const rozwinietaRef = useRef<HTMLDivElement>(null);
  const dotyk = useDotyk();
  const blysk = useBlyskZastapienia(areas);

  /** Dzielnica albo osiedle pod kursorem / z fokusem. */
  const [cel, setCel] = useState<SearchArea | null>(null);
  // Liczone z BIEŻĄCEJ listy, nie zapamiętane przy najechaniu: po kliknięciu cel jest już wybrany,
  // więc podgląd sam gaśnie, nawet jeśli kursor i fokus zostały na przycisku.
  const zastapione = useMemo(() => (cel && !dotyk ? dodajObszar(areas, cel).zastapione : []), [areas, cel, dotyk]);
  const przygaszone = new Set(zastapione);
  const kluczPodgladu = zastapione.join('|');
  const onPodgladRef = useRef(onPodglad);
  onPodgladRef.current = onPodglad;
  const bylPodglad = useRef('');
  useEffect(() => {
    if (kluczPodgladu === bylPodglad.current) return;
    bylPodglad.current = kluczPodgladu;
    onPodgladRef.current?.(kluczPodgladu ? kluczPodgladu.split('|') : []);
  }, [kluczPodgladu]);
  // Zamknięcie dymka pod kursorem nie wywoła `mouseleave` — bez tego chip u rodzica zostałby przekreślony.
  useEffect(
    () => () => {
      if (bylPodglad.current) onPodgladRef.current?.([]);
    },
    [],
  );

  const wybrane = useMemo(() => new Set(areas.map(areaKey)), [areas]);
  /** Ile osiedli każdej dzielnicy wybrano (kreska w kratce i „2 z 3 osiedli”). */
  const wybraneOsiedla = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of areas) {
      if (a.kind !== 'subdistrict') continue;
      const d = dzielnicaObszaru(a);
      if (d) m.set(d, (m.get(d) ?? 0) + 1);
    }
    return m;
  }, [areas]);
  const nicNieWybrano = !areas.some((a) => a.kind === 'district' || a.kind === 'subdistrict');

  // Osiedla rozwijają się POD dzielnicą, w siatce dwóch kolumn — przy dzielnicy z dołu listy
  // wypadają poza widoczny obszar dymka albo arkusza, więc dosuwamy je. `nearest`, nie `start`:
  // ma dojechać, nie przeskoczyć. jsdom nie zna `scrollIntoView`, stąd wywołanie warunkowe.
  useEffect(() => {
    if (rozwinieta) rozwinietaRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [rozwinieta]);

  const zastosuj = (wynik: ReturnType<typeof dodajObszar>) => {
    setLimit(wynik.limitOsiagniety);
    if (!wynik.limitOsiagniety && wynik.areas !== areas) onChange(wynik.areas);
  };

  const dotknijDzielnice = (d: string) => {
    if (wybrane.has(`d:${d}`)) {
      setLimit(false);
      onChange(usunObszar(areas, `d:${d}`));
      if (rozwinieta === d) setRozwinieta(null);
      return;
    }
    // Rozwija także wtedy, gdy limit nie wpuścił dzielnicy — człowiek widzi, co w niej jest,
    // a komunikat o limicie mówi, czemu nic się nie zaznaczyło (makieta robi tak samo).
    zastosuj(dodajObszar(areas, { kind: 'district', name: d }));
    if (!bezOsiedli) setRozwinieta(d);
  };

  const dotknijOsiedle = (o: string) => {
    if (wybrane.has(`s:${o}`)) {
      setLimit(false);
      onChange(usunObszar(areas, `s:${o}`));
      return;
    }
    zastosuj(dodajObszar(areas, { kind: 'subdistrict', name: o }));
  };

  // „Dowolna dzielnica” zdejmuje dzielnice i osiedla — to, co ten wybór obejmuje. Stacja metra
  // i narysowany obszar to inne rodzaje obszaru, spoza tej listy; zostają.
  const dowolna = () => {
    setLimit(false);
    setRozwinieta(null);
    if (!nicNieWybrano) onChange(areas.filter((a) => a.kind !== 'district' && a.kind !== 'subdistrict'));
  };

  const miasto = t(`search.area.miasto_mianownik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY });

  // Komunikat o limicie stoi TAM, GDZIE PATRZY CZŁOWIEK: w rozwiniętym pudełku osiedli, gdy jakieś
  // jest (dotknięta dzielnica właśnie je rozwinęła i dosunęła na ekran, a góra listy odjechała
  // poza telefon — sprawdzone na podglądzie), a nad listą tylko wtedy, gdy nic nie jest rozwinięte.
  const komunikatLimitu = limit && (
    <p role="status" className="rounded-md bg-warning-soft px-3 py-2 text-xs text-warning-soft-foreground">
      {t('search.area.limit_reached', { count: MAX_OBSZAROW })}
    </p>
  );

  return (
    <div data-testid="wybor-dzielnic">
      {!rozwinieta && komunikatLimitu && <div className="mb-2">{komunikatLimitu}</div>}
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
        <button
          type="button"
          aria-pressed={nicNieWybrano}
          onClick={dowolna}
          data-testid="dowolna-dzielnica"
          className="col-span-2 flex min-h-11 items-center gap-2.5 rounded-lg px-2 text-left text-base font-semibold outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted md:min-h-10 md:text-sm"
        >
          <Kratka stan={nicNieWybrano ? 'pelna' : 'pusta'} />
          {t('search.area.any_district', { miasto })}
        </button>
        {DZIELNICE.map((d) => {
          const cala = wybrane.has(`d:${d}`);
          const osiedla = bezOsiedli ? [] : (OSIEDLA.get(d) ?? []);
          const ileOsiedli = cala ? 0 : (wybraneOsiedla.get(d) ?? 0);
          const czesc = ileOsiedli > 0;
          const otwarta = rozwinieta === d;
          const swieci = blysk === `d:${d}`;
          return (
            <div key={d} className="contents">
              <div
                data-dzielnica={d}
                data-blysk={swieci || undefined}
                className={`flex min-h-11 items-center rounded-lg md:min-h-10 ${klasyZastapienia({
                  blysk: swieci,
                  tlo: otwarta ? 'bg-muted' : '',
                })}`}
              >
                <button
                  type="button"
                  aria-pressed={cala ? true : czesc ? 'mixed' : false}
                  onClick={() => dotknijDzielnice(d)}
                  {...zdarzeniaPodgladu({ kind: 'district', name: d }, setCel)}
                  className="flex min-h-11 min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2 py-1 text-left text-base outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted md:min-h-10 md:text-sm"
                >
                  <Kratka stan={cala ? 'pelna' : czesc ? 'czesciowa' : 'pusta'} przygaszona={przygaszone.has(`d:${d}`)} />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{d}</span>
                    {/* Widoczna linia zastępuje dawną ukrytą podpowiedź dla czytnika — jest w nazwie
                        przycisku, więc czytnik mówi ją raz, razem ze stanem „mieszany”. */}
                    {czesc && (
                      <span data-testid="licznik-osiedli" className="truncate text-xs font-medium text-primary">
                        {t('search.area.estates_partial', { n: ileOsiedli, count: osiedla.length })}
                      </span>
                    )}
                  </span>
                </button>
                {osiedla.length > 0 && (
                  <button
                    type="button"
                    aria-expanded={otwarta}
                    aria-label={t(otwarta ? 'search.area.collapse_estates' : 'search.area.expand_estates', { name: d })}
                    onClick={() => setRozwinieta(otwarta ? null : d)}
                    className="flex h-11 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted hover:text-foreground md:h-10"
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${otwarta ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
              {otwarta && osiedla.length > 0 && (
                <div
                  ref={rozwinietaRef}
                  data-testid="kontekst-dzielnicy"
                  className="col-span-2 mb-1.5 ml-2 space-y-2 border-l-2 border-primary py-2 pl-3 pr-1"
                >
                  {komunikatLimitu}
                  <p className="text-xs font-medium text-muted-foreground">{t('search.area.estates_in', { name: d })}</p>
                  {cala && (
                    <p data-testid="cala-dzielnica-podpowiedz" className="text-xs text-muted-foreground">
                      {t('search.area.whole_district_hint')}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {osiedla.map((o) => (
                      <ChipOsiedla
                        key={o}
                        nazwa={o}
                        aktywny={wybrane.has(`s:${o}`)}
                        przygaszony={przygaszone.has(`s:${o}`)}
                        blysk={blysk === `s:${o}`}
                        onClick={() => dotknijOsiedle(o)}
                        podglad={zdarzeniaPodgladu({ kind: 'subdistrict', name: o }, setCel)}
                      />
                    ))}
                  </div>
                  {dodatkiDzielnicy?.(d)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WyborDzielnic;
